'use client';
import { fetchReserveInfo } from '@/apis/happy/index.api';
import EmptyBox from '@/assets/icons/EmptyBox.svg';
import CDNImage from '@/components/cdnImage/cdnImage';
import JDIPrice from '@/components/price';
import { useTranslation } from '@/i18n/client';
import { EXPOSURE_RECORD } from '@/tracks/23622';
import { useEffect, useRef, useState } from 'react';
import Dot from '../icon/dot.svg';
import style from './MyReservation.module.scss';

interface ReservationProps {
  data: any[];
  currentMills: number;
  localInfo: any;
}

interface BatchProps {
  batch: any;
  localInfo: any;
  currentMills: number;
}

interface CountDownProps {
  beginTime: string;
  currentMills: number;
}

// 独立的倒计时组件
const CountDownComponent = ({ beginTime, currentMills }: CountDownProps) => {
  const [hour, setHour] = useState('00');
  const [minute, setMinute] = useState('00');
  const [sec, setSec] = useState('00');
  const cuPageRef = useRef(currentMills || new Date().getTime());

  const CountDown = () => {
    const beginDate = new Date(beginTime).getTime() * 1000;
    const now = cuPageRef.current;
    const remainingTime = beginDate - now;
    if (remainingTime > 0) {
      const totalSeconds = Math.floor(remainingTime / 1000);
      setHour(String(Math.floor(totalSeconds / 3600)).padStart(2, '0'));
      setMinute(String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0'));
      setSec(String(totalSeconds % 60).padStart(2, '0'));
    } else {
      setHour('00');
      setMinute('00');
      setSec('00');
      location.reload();
    }
    cuPageRef.current = now + 1000;
  };

  useEffect(() => {
    CountDown();
    const timer = setInterval(CountDown, 1000);
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [beginTime]);

  return (
    <div className={style['timeContainer']}>
      <span className={style['calculateTime']}>{hour}</span>
      <span className={style['colon']}> : </span>
      <span className={style['calculateTime']}>{minute}</span>
      <span className={style['colon']}> : </span>
      <span className={style['calculateTime']}>{sec} </span>
    </div>
  );
};

const BatchList = (props: BatchProps) => {
  const { batch, currentMills, localInfo } = props;
  const skus = batch?.skus || [];
  const batchName = batch?.batchName || '';
  const isCountDown = batch?.isCountDown;

  const trackParams = (index: number, item: any) => {
    return {
      skuid: item.skuId.toString(),
      firprice: isNaN(item?.realPrice) ? -100 : Number(item?.realPrice),
      firpricetype: '11',
      index: index,
      floorid: batch.groupId,
    };
  };
  return (
    <div className={style.batchContainer}>
      <div className={style.batchStatus}>
        <Dot />
        {isCountDown ? (
          <CountDownComponent beginTime={batch.beginTime} currentMills={currentMills} />
        ) : (
          <p>{batchName}</p>
        )}
      </div>
      {skus.map((sku: any, index: number) => (
        <div
          exptag={'exp|' + EXPOSURE_RECORD.Activity_FlashSales_MyReserve}
          data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
          data-exptag-json-param={JSON.stringify(trackParams(index, sku))}
          className={style.products}
          key={index}
        >
          <div className={style.img}>
            <CDNImage src={sku.skuImg} width={120} height={120} />
          </div>
          <div className={style.productInfo}>
            <p>{sku.skuName}</p>
            <div className={style.price}>
              <JDIPrice
                locale={localInfo?.languageCode}
                currency={localInfo?.currencyCode}
                price={sku.realPrice || ''}
                uiType={2}
                containerStyle={{
                  fontSize: '18px',
                  lineHeight: '24px',
                  fontWeight: '700',
                  color: '#1A1A1A',
                }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const MyReservation = (props: ReservationProps) => {
  const { currentMills, localInfo } = props;
  const [dataReady, setDataReady] = useState(false);
  const [data, setData] = useState<any>([]);
  const { t } = useTranslation();

  // 再次调取预约数据，保证数据实时性
  const genReserveData = () => {
    fetchReserveInfo(5).then((res) => {
      if (res?.code === '200') {
        if (res.data && res.data.length > 0) {
          setData(res.data);
        }
      } else {
        console.log('---- cjy ---- My reservation', res.msg);
      }
    });
  };

  if (!dataReady) {
    genReserveData();
    setDataReady(true);
  }

  return (
    <div className={style.container}>
      {data.length === 0 ? (
        // 无预约数据
        <div
          exptag={'exp|' + EXPOSURE_RECORD.Activity_FlashSales_MyReserve_Empty}
          data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
          className={style.empty}
        >
          <EmptyBox width={100} height={100} />
          <br />
          {t('flashSales.empty')}
        </div>
      ) : (
        // 各场次预约数据
        data.map((item: any, index: number) => (
          <div className={style.container} key={index}>
            <BatchList batch={item} currentMills={currentMills} localInfo={localInfo} />
          </div>
        ))
      )}
    </div>
  );
};

export default MyReservation;
